import type {
  CopyPricePreviewResponse,
  CopyPriceRequest,
  CopyPriceResponse,
} from '@/api';
import { getApiClient } from '@/api/client';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import type { NormalizedError } from '@/utils/error-utils';
import { normalizeApiError } from '@/utils/error-utils';
import { useMutation, useQuery } from '@tanstack/react-query';

const DEFAULT_DEBOUNCE_MS = 300;

export const useEligibleTargets = (
  sourcePriceUnitId: number | null,
  searchField: string,
  debounceMs = DEFAULT_DEBOUNCE_MS,
) => {
  const debouncedSearch = useDebouncedValue(searchField, debounceMs);
  return useQuery({
    queryKey: ['eligibleTargets', sourcePriceUnitId, debouncedSearch],
    queryFn: async () => {
      if (sourcePriceUnitId === null) throw new Error('No source price unit');
      return getApiClient().copyPriceEligibleTargetsSourcePriceUnitId({
        sourcePriceUnitId,
        searchField: debouncedSearch || undefined,
      });
    },
    enabled: sourcePriceUnitId !== null && !!debouncedSearch,
    retry: false,
  });
};

export const useCopyPricePreview = (
  onSuccess: (data: CopyPricePreviewResponse) => void,
  onError: (error: NormalizedError) => void,
) =>
  useMutation({
    mutationFn: (copyPriceRequest: CopyPriceRequest) =>
      getApiClient().copyPricePreview({ copyPriceRequest }),
    onSuccess,
    onError: (err) => onError(normalizeApiError(err)),
  });

export const useCopyPrice = (
  onSuccess: (data: CopyPriceResponse) => void,
  onError: (error: NormalizedError) => void,
) =>
  useMutation({
    mutationFn: (copyPriceRequest: CopyPriceRequest) =>
      getApiClient().copyPrice({ copyPriceRequest }),
    onSuccess,
    onError: (err) => onError(normalizeApiError(err)),
  });
